import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Alert, Spinner, Image, ListGroup, Form, InputGroup } from 'react-bootstrap';
import { FaUserShield, FaUser, FaEnvelope, FaPhone, FaUsers, FaMoneyBillWave, FaToggleOn, FaToggleOff, FaChild, FaEye, FaSearch, FaTimes, FaSync } from 'react-icons/fa';
import axios from 'axios';

import './Users.css';
import { AuthContext } from '../../contexts/AuthContext';

const Users = () => {
  const { user, token } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  // State for filtered users (what we display after applying filters)
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
  const [showDependentsModal, setShowDependentsModal] = useState(false);
  const [selectedUserForDependents, setSelectedUserForDependents] = useState(null);
  // State for refresh button
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    role: ''
  });

  // Check if current user is superadmin
  const isSuperAdmin = user?.email === 'superadmin@aceswa.org';

  // Create axios instance with default headers
  const api = axios.create({
    baseURL: 'https://aceswafund.org/welfare/welfare-system',
    headers: {
       'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
    }
  });

  useEffect(() => {
    if (token) {
      fetchUsers();
    }
  }, [token]);

  // Apply filters whenever filters state or users array changes
  useEffect(() => {
    applyFilters();
  }, [users, filters]);

  const fetchUsers = async (showRefreshAnimation = false) => {
    // If showRefreshAnimation is true, we're using the refresh button
    if (showRefreshAnimation) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await api.get('admin-get-users');
      
      if (response.data.status) {
        console.log('Users data:', response.data.users); // Debug log
        setUsers(response.data.users);
        setError(null);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      if (err.response?.status === 401) {
        setError('Unauthorized access. Please login again.');
      } else {
        setError('Error connecting to server');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Function to handle refresh button click
  const handleRefresh = () => {
    fetchUsers(true);
  };

  // Function to apply all active filters
  const applyFilters = () => {
    let result = [...users];

    // Filter by name (case-insensitive partial match)
    if (filters.name) {
      result = result.filter(user => 
        user.full_name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    // Filter by email (case-insensitive partial match)
    if (filters.email) {
      result = result.filter(user => 
        user.email.toLowerCase().includes(filters.email.toLowerCase())
      );
    }

    // Filter by role (exact match)
    if (filters.role) {
      result = result.filter(user => 
        user.role === filters.role
      );
    }

    setFilteredUsers(result);
  };

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Clear a specific filter
  const clearFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: ''
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      name: '',
      email: '',
      role: ''
    });
  };

  const handleMakeAdmin = (user) => {
    setSelectedUser(user);
    setActionType('make-admin');
    setShowModal(true);
    setActionMessage({ type: '', text: '' });
  };

  const handleMakeUser = (user) => {
    setSelectedUser(user);
    setActionType('make-user');
    setShowModal(true);
    setActionMessage({ type: '', text: '' });
  };

  const handleShowDependents = (user) => {
    setSelectedUserForDependents(user);
    setShowDependentsModal(true);
  };

  const confirmAction = async () => {
    setActionLoading(true);
    setActionMessage({ type: '', text: '' });

    try {
      if (actionType === 'make-admin') {
        const response = await api.post('make-admin', { 
          user_id: selectedUser.user_id 
        });
        
        if (response.data.status) {
          setActionMessage({ type: 'success', text: response.data.message });
          // Update user role in the list
          setUsers(users.map(u => 
            u.user_id === selectedUser.user_id 
              ? { ...u, role: 'admin' } 
              : u
          ));
          setTimeout(() => {
            setShowModal(false);
            setActionMessage({ type: '', text: '' });
          }, 2000);
        } else {
          setActionMessage({ type: 'danger', text: response.data.message });
        }
      } else if (actionType === 'make-user') {
        const response = await api.get(`make-user?user_id=${selectedUser.user_id}`);
        
        if (response.data.status) {
          setActionMessage({ type: 'success', text: response.data.message });
          // Update user role in the list
          setUsers(users.map(u => 
            u.user_id === selectedUser.user_id 
              ? { ...u, role: 'staff' } 
              : u
          ));
          setTimeout(() => {
            setShowModal(false);
            setActionMessage({ type: '', text: '' });
          }, 2000);
        } else {
          setActionMessage({ type: 'danger', text: response.data.message });
        }
      }
    } catch (err) {
      console.error('Error in action:', err);
      let errorMessage = 'Error processing request';
      
      if (err.response?.status === 401) {
        errorMessage = 'Unauthorized. Only superadmin can perform this action.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setActionMessage({ 
        type: 'danger', 
        text: errorMessage
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    switch(role) {
      case 'superadmin':
        return <Badge bg="danger">Super Admin</Badge>;
      case 'admin':
        return <Badge bg="warning" text="dark">Admin</Badge>;
      case 'staff':
        return <Badge bg="info">Staff</Badge>;
      default:
        return <Badge bg="secondary">{role}</Badge>;
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? 
      <Badge bg="success"><FaToggleOn /> Active</Badge> : 
      <Badge bg="secondary"><FaToggleOff /> Inactive</Badge>;
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setActionType('');
    setActionMessage({ type: '', text: '' });
  };

  const handleCloseDependentsModal = () => {
    setShowDependentsModal(false);
    setSelectedUserForDependents(null);
  };

  // Show loading if no token
  if (!token) {
    return (
      <Container className="text-center mt-5">
        <Alert variant="warning">
          Please login to access this page
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading users...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => fetchUsers()} className="me-2">
            Try Again
          </Button>
          <Button variant="outline-secondary" onClick={handleRefresh}>
            <FaSync className={`me-2 ${refreshing ? 'fa-spin' : ''}`} />
            Refresh
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="users-container py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-title">
                <FaUsers className="me-2" />
                User Management
              </h2>
              <p className="text-muted mb-0">
                Logged in as: <strong>{user?.email}</strong> ({getRoleBadge(user?.role)})
              </p>
            </div>
            {/* Refresh Button */}
            <Button 
              variant="outline-primary" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="refresh-button"
            >
              <FaSync className={`me-2 ${refreshing ? 'fa-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </div>
        </Col>
      </Row>

      {/* Filter Section */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm filter-card">
            <Card.Body>
              <Row>
                <Col md={12} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="filter-title mb-0">
                      <FaSearch className="me-2" />
                      Filter Users
                    </h5>
                    {/* Last Updated Indicator - Optional */}
                    <small className="text-muted">
                      Last updated: {new Date().toLocaleTimeString()}
                    </small>
                  </div>
                </Col>
              </Row>
              <Row>
                {/* Name Filter */}
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Filter by Name</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaUser />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="name"
                        value={filters.name}
                        onChange={handleFilterChange}
                        placeholder="Enter name..."
                      />
                      {filters.name && (
                        <Button 
                          variant="outline-secondary"
                          onClick={() => clearFilter('name')}
                        >
                          <FaTimes />
                        </Button>
                      )}
                    </InputGroup>
                  </Form.Group>
                </Col>

                {/* Email Filter */}
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Filter by Email</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaEnvelope />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="email"
                        value={filters.email}
                        onChange={handleFilterChange}
                        placeholder="Enter email..."
                      />
                      {filters.email && (
                        <Button 
                          variant="outline-secondary"
                          onClick={() => clearFilter('email')}
                        >
                          <FaTimes />
                        </Button>
                      )}
                    </InputGroup>
                  </Form.Group>
                </Col>

                {/* Role Filter */}
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Filter by Role</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaUserShield />
                      </InputGroup.Text>
                      <Form.Select
                        name="role"
                        value={filters.role}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Roles</option>
                        <option value="superadmin">Super Admin</option>
                        <option value="admin">Admin</option>
                        <option value="staff">Staff</option>
                      </Form.Select>
                      {filters.role && (
                        <Button 
                          variant="outline-secondary"
                          onClick={() => clearFilter('role')}
                        >
                          <FaTimes />
                        </Button>
                      )}
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Filter Status and Clear Button */}
              <Row>
                <Col md={12} className="d-flex justify-content-between align-items-center">
                  <div className="filter-stats">
                    <Badge bg="info" className="me-2">
                      Total Users: {users.length}
                    </Badge>
                    <Badge bg="success">
                      Filtered Results: {filteredUsers.length}
                    </Badge>
                  </div>
                  <div>
                    {(filters.name || filters.email || filters.role) && (
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={clearAllFilters}
                        className="me-2"
                      >
                        <FaTimes className="me-2" />
                        Clear All Filters
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              {/* Table Header with Refresh Status */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-0">Users List</h6>
                </div>
                {refreshing && (
                  <div className="text-primary">
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Updating data...
                  </div>
                )}
              </div>

              <div className="table-responsive">
                <Table hover className="users-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Contact</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Contributions</th>
                      <th>Dependents</th>
                      <th>Next of Kin</th>
                      {isSuperAdmin && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Use filteredUsers instead of users */}
                    {filteredUsers.map((user) => (
                      <tr key={user.user_id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <Image
                              src={user.profile_image} 
                              roundedCircle 
                              className="user-avatar me-2"
                              width="40"
                              height="40"
                              onError={(e) => {
                                e.target.src = 'https://aceswafund.org/welfare/welfare-system/uploads/profile_images/default.png';
                              }}
                            />
                            <div>
                              <strong>{user.full_name}</strong>
                              <div className="text-muted small">
                                <FaEnvelope className="me-1" size={10} />
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {user.phone ? (
                            <div><FaPhone className="me-1" size={12} /> {user.phone}</div>
                          ) : (
                            <span className="text-muted">No phone</span>
                          )}
                        </td>
                        <td>{getRoleBadge(user.role)}</td>
                        <td>{getStatusBadge(user.is_active)}</td>
                        <td>
                          <div><strong>GH₵ {parseFloat(user.total_contribution || 0).toFixed(2)}</strong></div>
                          <small className="text-muted">{user.total_contributions_count || 0} contributions</small>
                        </td>
                        <td>
                          {user.dependents && user.dependents.length > 0 ? (
                            <Button
                              variant="link"
                              className="p-0 text-decoration-none"
                              onClick={() => handleShowDependents(user)}
                            >
                              <Badge bg="info" pill className="d-flex align-items-center gap-1">
                                <FaChild size={10} />
                                {user.dependents.length} Dependent{user.dependents.length !== 1 ? 's' : ''}
                                <FaEye size={10} className="ms-1" />
                              </Badge>
                            </Button>
                          ) : (
                            <span className="text-muted">
                              <Badge bg="secondary" pill>None</Badge>
                            </span>
                          )}
                        </td>
                        <td>
                          {user.next_of_kin ? (
                            <div>
                              <div><strong>{user.next_of_kin.full_name}</strong></div>
                              <small className="text-muted">
                                {user.next_of_kin.relationship}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">Not set</span>
                          )}
                        </td>
                        {isSuperAdmin && (
                          <td>
                            {user.email !== 'superadmin@aceswa.com' && (
                              <div className="action-buttons">
                                {user.role !== 'admin' && user.role !== 'superadmin' && (
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleMakeAdmin(user)}
                                    className="me-2"
                                    title="Make Admin"
                                  >
                                    <FaUserShield /> Admin
                                  </Button>
                                )}
                                {user.role === 'admin' && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleMakeUser(user)}
                                    title="Make Regular User"
                                  >
                                    <FaUser /> User
                                  </Button>
                                )}
                              </div>
                            )}
                            {user.email === 'superadmin@aceswa.com' && (
                              <Badge bg="danger">Super Admin</Badge>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {filteredUsers.length === 0 && (
                <Alert variant="info" className="text-center">
                  {users.length === 0 ? 'No users found' : 'No users match the current filters'}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirmation Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === 'make-admin' ? 'Make Admin' : 'Make Regular User'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionMessage.text && (
            <Alert variant={actionMessage.type}>
              {actionMessage.text}
            </Alert>
          )}
          
          {!actionMessage.text && selectedUser && (
            <>
              <p>
                Are you sure you want to {actionType === 'make-admin' ? 'promote' : 'demote'}{' '}
                <strong>{selectedUser.full_name}</strong> 
                {actionType === 'make-admin' ? ' to Admin' : ' to regular User'}?
              </p>
              <div className="border rounded p-3 bg-light">
                <p className="mb-1"><strong>Current Role:</strong> {getRoleBadge(selectedUser.role)}</p>
                <p className="mb-0"><strong>Email:</strong> {selectedUser.email}</p>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={actionLoading}>
            {actionMessage.text ? 'Close' : 'Cancel'}
          </Button>
          {!actionMessage.text && (
            <Button 
              variant={actionType === 'make-admin' ? 'success' : 'warning'}
              onClick={confirmAction}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">Processing...</span>
                </>
              ) : (
                actionType === 'make-admin' ? 'Make Admin' : 'Make User'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Dependents Modal */}
      <Modal show={showDependentsModal} onHide={handleCloseDependentsModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaChild className="me-2" />
            Dependents - {selectedUserForDependents?.full_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUserForDependents?.dependents && selectedUserForDependents.dependents.length > 0 ? (
            <ListGroup>
              {selectedUserForDependents.dependents.map((dependent, index) => (
                <ListGroup.Item key={dependent.id || index} className="d-flex justify-content-between align-items-start">
                  <div className="ms-2 me-auto">
                    <div className="fw-bold">{dependent.name}</div>
                    <div className="text-muted small">
                      <span className="me-3">Relationship: {dependent.relationship}</span>
                      {dependent.date_of_birth_formatted && (
                        <span>DOB: {dependent.date_of_birth_formatted}</span>
                      )}
                    </div>
                  </div>
                  <Badge bg="info" pill>Dependent</Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <Alert variant="info">
              No dependents found for this user.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDependentsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Users;